import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const MROEngineerCompletion = ({ open, onClose, workOrder, onSuccess }) => {
  const [decision, setDecision] = useState('COMPLETE');
  const [notes, setNotes] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingCrs, setUploadingCrs] = useState(false);
  const [crsFile, setCrsFile] = useState(null);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const handleAuthenticatedDownload = async (url, filenameFallback) => {
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filenameFallback);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to download CRS file';
      setError(message);
      showToast(message, 'error');
    }
  };

  const handleUploadCrs = async () => {
    if (!crsFile || !workOrder?._id) {
      setError('Please choose a CRS file first.');
      return;
    }
    try {
      setUploadingCrs(true);
      setError('');
      const form = new FormData();
      form.append('crsFile', crsFile);
      await axios.post(`/api/maintenance/${workOrder._id}/mro-engineer/crs`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('CRS uploaded successfully', 'success');
      setCrsFile(null);
      onSuccess();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to upload CRS file';
      setError(message);
      showToast(message, 'error');
    } finally {
      setUploadingCrs(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const cleanedRemarks = remarks.trim();
      if (decision === 'REJECT' && !cleanedRemarks) {
        setError('Rejection remarks are required');
        return;
      }

      await axios.post(`/api/maintenance/${workOrder._id}/mro-engineer/complete`, {
        decision,
        notes: notes.trim() || undefined,
        remarks: cleanedRemarks || undefined
      });

      showToast(decision === 'REJECT' ? 'Step rejected successfully' : 'Work marked as completed successfully', 'success');
      onSuccess();
      handleClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to mark work as completed';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDecision('COMPLETE');
    setNotes('');
    setRemarks('');
    setError('');
    setCrsFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>MRO_ENGINEER Completion</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Work Order: <strong>{workOrder?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aircraft: <strong>{workOrder?.aircraftId?.registration || 'Unknown'}</strong>
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Decision
          </Typography>
          <ToggleButtonGroup
            value={decision}
            exclusive
            onChange={(e, v) => v && setDecision(v)}
            fullWidth
            size="small"
          >
            <ToggleButton value="COMPLETE">Complete</ToggleButton>
            <ToggleButton value="REJECT" color="error">Reject</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Completion Notes (Optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any notes about the completed work..."
          sx={{ mt: 2 }}
        />
        {decision === 'REJECT' && (
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Rejection Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Explain why this step is rejected and what needs correction..."
            sx={{ mt: 2 }}
          />
        )}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            CRS Document (Optional)
          </Typography>
          {workOrder?.crsDocument?.originalName && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleAuthenticatedDownload(
                `/api/maintenance/${workOrder._id}/crs/download`,
                workOrder.crsDocument.originalName || `crs-${workOrder._id}`
              )}
              sx={{ mr: 1, mb: 1 }}
            >
              Download Current CRS
            </Button>
          )}
          <Button
            variant="outlined"
            component="label"
            size="small"
            sx={{ mr: 1, mb: 1 }}
            disabled={uploadingCrs}
          >
            Select CRS File
            <input
              type="file"
              hidden
              onChange={(e) => setCrsFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleUploadCrs}
            disabled={uploadingCrs || !crsFile}
            sx={{ mb: 1 }}
          >
            {uploadingCrs ? 'Uploading...' : 'Upload CRS'}
          </Button>
          {crsFile && (
            <Typography variant="caption" display="block" color="text.secondary">
              Selected: {crsFile.name}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color={decision === 'REJECT' ? 'error' : 'primary'} disabled={loading || (decision === 'REJECT' && !remarks.trim())}>
          {loading ? 'Submitting...' : (decision === 'REJECT' ? 'Reject' : 'Mark as Completed')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MROEngineerCompletion;

